'use client';

import React, { useState } from 'react';
import MuiModal from '../../mui/MuiModal';
import { createBundleObject, setDocumentOptionCookie } from '../../lib/helpers';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { Button } from '../../blocks/Button';
import styles from './SuggestDocumentCreateModal.module.scss';

export type SuggestDocumentCreateModalData<T extends boolean> = T extends true
	? {
			contactID: string;
			eventDate?: string | Date | null;
			dueDate?: string | Date | null;
			title?: string | null;
	  }
	: {
			contactID: string;
			eventDate?: string | Date | null;
			title?: string | null;
	  };

export const suggestDocumentCreateModalCookieKey = 'suggestDocumentCreateModal';

// we don't care about contactID here because we only have the one key, so we just check if the cookie exists
export const suggestDocumentCreateModalCookieDataExists = () =>
	typeof window === 'undefined'
		? false
		: !!localStorage.getItem( suggestDocumentCreateModalCookieKey );

export const getSuggestDocumentCreateModalData = <T extends boolean>(
	isInvoice: T
) => {
	const suggestDocumentCreateModalData =
		typeof window === 'undefined'
			? undefined
			: localStorage.getItem( suggestDocumentCreateModalCookieKey );
	if ( suggestDocumentCreateModalData ) {
		const data: SuggestDocumentCreateModalData<T> = JSON.parse(
			suggestDocumentCreateModalData
		);
		return data;
	}
};

const setSuggestDocumentCreateModalData = <T extends boolean>(
	data: SuggestDocumentCreateModalData<T>
) =>
	/* eslint-disable indent */
	localStorage.setItem(
		suggestDocumentCreateModalCookieKey,
		JSON.stringify( data )
	);
/* eslint-enable indent */

// After sending a document we remove the suggest document create modal data to clean the slate for the next document
export const updateSuggestDocumentCreateModalDataAfterSending = <
	T extends boolean
>(
	/* eslint-disable indent */
	isInvoice?: T
) => {
	/* eslint-enable indent */
	const existingData = getSuggestDocumentCreateModalData( isInvoice || false );
	if ( existingData ) {
		localStorage.removeItem( suggestDocumentCreateModalCookieKey );
	}
};

export const clearSuggestDocumentCreateModalData = () =>
	localStorage.removeItem( suggestDocumentCreateModalCookieKey );

export enum DocumentTypeEnum {
	Contract = 'Contract',
	Invoice = 'Invoice',
	Proposal = 'Proposal',
}

export type SuggestDocumentCreateModalProps = {
	open: boolean;
	onClose?: () => void;
	contact?: string;
	eventDate?: string | Date | null;
	dueDate?: string | Date | null;
	title?: string | null;
	nextDocumentType?: DocumentTypeEnum;
	previousDocumentType: DocumentTypeEnum;
};

const SuggestDocumentCreateModal: React.FC<SuggestDocumentCreateModalProps> = ( {
	onClose,
	contact,
	eventDate,
	dueDate,
	title,
	open: openProp,
	nextDocumentType,
	previousDocumentType,
} ) => {
	const router = useBlockingRouter();

	const [ open, setOpen ] = useState( openProp );

	// A helper to make the JSX less...JS-like and more X like
	const onClick = () => {
		// If we have a contact prop, then lets use it to pre-fill the contact on the contract page via a bundle cookie
		if ( contact ) {
			// We only want to create a _new_ bundle cookie if one doesn't already exist
			const bundle = createBundleObject( contact );
			if ( !bundle ) {
				// Don't use cookies for this one-off bundle because then the bundle emails are sent. Instead use localStorage.
				setSuggestDocumentCreateModalData( {
					contactID: contact,
					eventDate,
					dueDate,
					title,
				} );
			}
		}
		const route =
			nextDocumentType === DocumentTypeEnum.Contract
				? '/contract/create'
				: '/invoice/create';
		router.push( route );
		setOpen( false );
		onClose?.();
	};

	const handleClose = () => {
		// clear the cookie
		setDocumentOptionCookie();
		setOpen( false );
		onClose?.();
	};

	const getHeaderCopy = () => {
		const previously =
			previousDocumentType === DocumentTypeEnum.Contract
				? 'Contract'
				: 'Proposal';
		if ( nextDocumentType === DocumentTypeEnum.Contract ) {
			return `${ previously }, check! Next step, contract.`;
		} else if ( nextDocumentType === DocumentTypeEnum.Invoice ) {
			return `${ previously }, check! Next step, invoice.`;
		}
	};

	const getButtonText = () => {
		if ( nextDocumentType === DocumentTypeEnum.Contract ) {
			return 'Create Contract';
		} else if ( nextDocumentType === DocumentTypeEnum.Invoice ) {
			return 'Create Invoice';
		}
	};

	return (
		<MuiModal
			muiModalProps={ {
				open,
				onClose: handleClose,
				slotProps: { paper: { 'data-cy': 'suggest-document-create-modal' } },
			} }
			title={ getHeaderCopy() }
			controls={
				<form
					className={ styles.container }
					onSubmit={ ( e ) => {
						e.preventDefault();
						e.stopPropagation();
					} }
				>
					<Button
						variant='contained'
						color='primary'
						type='submit'
						onClick={ onClick }
					>
						{ getButtonText() }
					</Button>
					<Button variant='outlined' color='secondary' onClick={ handleClose }>
						Not right now
					</Button>
				</form>
			}
		></MuiModal>
	);
};

export default SuggestDocumentCreateModal;
